/**
 * 顶部栏组件 - 参考 makeverse.cn 设计风格
 *
 * 提供工作区页面的顶部导航功能：
 * - 左侧：返回按钮（根据当前视图决定行为）
 * - 中间：创作树切换标签
 * - 右侧：工作区标题
 *
 * 返回按钮行为：
 * - 主视图：返回作者中心
 * - 创作树视图：返回工作区主视图
 */

import React from 'react';
import { ListTree, ArrowLeft } from 'lucide-react';

// ==================== 类型定义 ====================

interface TopBarProps {
  /** 返回作者中心回调（主视图下使用） */
  onBack: () => void;
  /** 返回工作区主视图回调（创作树视图下使用） */
  onBackToMain?: () => void;
  /** 创作树按钮点击回调 */
  onCreativeTree: () => void;
  /** 当前是否在创作树视图 */
  isCreativeTreeActive?: boolean;
  /** 工作区名称 */
  workspaceName?: string;
}

// ==================== 主组件 ====================

/**
 * 顶部栏组件
 *
 * @param {TopBarProps} props - 组件属性
 * @returns {JSX.Element} 顶部栏组件
 */
export const TopBar: React.FC<TopBarProps> = ({
  onBack,
  onBackToMain,
  onCreativeTree,
  isCreativeTreeActive = false,
  workspaceName,
}) => {
  // 判断是否在子视图中（创作树）
  const isInSubView = isCreativeTreeActive;

  // 返回按钮的行为：子视图返回主视图，主视图返回作者中心
  const handleBack = isInSubView ? (onBackToMain || onBack) : onBack;

  // 返回按钮的标签
  const backLabel = isInSubView ? '返回工作区' : '返回作者中心';

  return (
    <div className="h-16 bg-white border-b border-gray-100 flex items-center px-6 flex-shrink-0 shadow-sm">
      {/* 左侧：返回按钮 */}
      <div className="flex items-center w-48">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-150 group"
          aria-label={backLabel}
          title={backLabel}
        >
          <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-0.5" />
          <span className="text-sm font-medium">{backLabel}</span>
        </button>
      </div>

      {/* 中间：视图切换标签 */}
      <div className="flex-1 flex justify-center">
        <div className="flex items-center bg-gray-100 p-1 rounded-xl">
          {/* 创作树标签 */}
          <button
            onClick={onCreativeTree}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg transition-all duration-150 ${
              isCreativeTreeActive
                ? 'bg-white text-gray-800 shadow-sm font-medium'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            aria-label="创作树"
            title="创作树"
          >
            <ListTree className="w-4 h-4" />
            <span className="text-sm">创作树</span>
          </button>
        </div>
      </div>

      {/* 右侧：工作区名称 */}
      <div className="flex items-center w-48 justify-end">
        {workspaceName && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
            <div className="w-2 h-2 rounded-full bg-ink-800" />
            <span className="text-sm text-gray-600 font-medium truncate max-w-[140px]">
              {workspaceName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBar;
